/**
 * Central, editable content for Rombex.
 * Every navigation item and sub-page reads from this file, so updating copy,
 * links or adding new menu entries only requires editing here.
 */

export type MenuItem = {
  title: string
  description: string
  href: string
  icon: string // lucide-react icon name
}

export type MenuGroup = {
  label: string
  items: MenuItem[]
}

export const solutionsMenu: MenuItem[] = [
  {
    title: 'Cross-border Payments',
    description: 'Move fiat and stablecoins across multiple currencies.',
    href: '/solutions/cross-border-payments',
    icon: 'Globe2',
  },
  {
    title: 'Global Payouts',
    description: 'Pay contractors, employees, creators and marketplaces worldwide.',
    href: '/solutions/global-payouts',
    icon: 'Send',
  },
  {
    title: 'Payment Collections',
    description: 'Collect local payments from customers globally.',
    href: '/solutions/payment-collections',
    icon: 'Download',
  },
  {
    title: 'Merchant Payments',
    description: 'Online checkout, payment links, QR payments and invoicing.',
    href: '/solutions/merchant-payments',
    icon: 'ShoppingCart',
  },
  {
    title: 'Treasury',
    description: 'Manage liquidity across multiple currencies.',
    href: '/solutions/treasury',
    icon: 'Landmark',
  },
  {
    title: 'Crypto & DeFi',
    description: 'Built on modular stablecoin infrastructure.',
    href: '/solutions/crypto-defi',
    icon: 'Bitcoin',
  },
  {
    title: 'Banking Infrastructure',
    description: 'Virtual accounts, local rails and bank integrations.',
    href: '/solutions/banking-infrastructure',
    icon: 'Building2',
  },
]

export const productsMenu: MenuItem[] = [
  {
    title: 'Orchestration',
    description: 'Move money intelligently across payment rails.',
    href: '/products/orchestration',
    icon: 'Workflow',
  },
  {
    title: 'Wallets',
    description: 'Securely hold and manage fiat and stablecoins.',
    href: '/products/wallets',
    icon: 'Wallet',
  },
  {
    title: 'Virtual Accounts',
    description: 'Receive payments locally in multiple countries.',
    href: '/products/virtual-accounts',
    icon: 'CreditCard',
  },
  {
    title: 'Payment APIs',
    description: 'Developer-first APIs for money movement.',
    href: '/products/payment-apis',
    icon: 'Code2',
  },
  {
    title: 'Merchant Dashboard',
    description: 'Complete business dashboard.',
    href: '/products/merchant-dashboard',
    icon: 'LayoutDashboard',
  },
  {
    title: 'User Wallet',
    description: 'Personal wallet experience.',
    href: '/products/user-wallet',
    icon: 'Smartphone',
  },
  {
    title: 'Compliance Engine',
    description: 'KYC, KYB and AML built in.',
    href: '/products/compliance-engine',
    icon: 'ShieldCheck',
  },
  {
    title: 'Settlement Engine',
    description: 'Fast settlement infrastructure.',
    href: '/products/settlement-engine',
    icon: 'Zap',
  },
  {
    title: 'Analytics',
    description: 'Reporting and payment insights.',
    href: '/products/analytics',
    icon: 'BarChart3',
  },
]

export const resourcesMenu: MenuItem[] = [
  { title: 'Blog', description: 'Product news and industry insights.', href: '/resources/blog', icon: 'Newspaper' },
  { title: 'Guides', description: 'Step-by-step integration guides.', href: '/resources/guides', icon: 'BookOpen' },
  {
    title: 'Customer Stories',
    description: 'How teams scale globally with Rombex.',
    href: '/resources/customer-stories',
    icon: 'Quote',
  },
  {
    title: 'Documentation',
    description: 'Full technical documentation.',
    href: '/docs',
    icon: 'FileText',
  },
  {
    title: 'Support Center',
    description: 'Help articles and support.',
    href: '/resources/support-center',
    icon: 'LifeBuoy',
  },
  { title: 'Status Page', description: 'Real-time platform status.', href: '/resources/status-page', icon: 'Activity' },
  {
    title: 'Developers',
    description: 'SDKs, references and tooling.',
    href: '/resources/developers',
    icon: 'Terminal',
  },
  {
    title: 'Community',
    description: 'Join the Rombex builder community.',
    href: '/resources/community',
    icon: 'Users',
  },
]

export const EXTERNAL = {
  merchant: 'https://merchant.rombex.com/',
  user: 'https://rombex-user.vercel.app/',
  requestDemo: '/contact?intent=demo',
}

/* ---------------- Page content (editable) ---------------- */

export type FeatureBlock = { title: string; body: string; icon: string }

export type PageContent = {
  eyebrow: string
  title: string
  subtitle: string
  icon: string
  bullets: string[]
  features: FeatureBlock[]
  stat?: { value: string; label: string }[]
}

function findMenuItem(href: string): MenuItem | undefined {
  return [...solutionsMenu, ...productsMenu, ...resourcesMenu].find((m) => m.href === href)
}

/** Build a consistent, editable page from a slug section + slug. */
export const pageContent: Record<string, PageContent> = {
  '/solutions/cross-border-payments': {
    eyebrow: 'Solutions',
    title: 'Cross-border payments, settled in minutes',
    subtitle:
      'Move fiat and stablecoins across multiple currencies with a single integration. Reach hard-to-serve corridors without holding pre-funded accounts everywhere.',
    icon: 'Globe2',
    bullets: [
      'Settle across 40+ currencies and major stablecoins',
      'Automatic FX and best-rate routing across rails',
      'Local payout rails in emerging and established markets',
      'Transparent, upfront pricing on every transfer',
    ],
    features: [
      { title: 'Multi-rail routing', body: 'Automatically pick the fastest, cheapest path between banking and stablecoin rails.', icon: 'Workflow' },
      { title: 'Real-time FX', body: 'Lock competitive rates at the moment of transfer with full visibility.', icon: 'RefreshCw' },
      { title: 'Global reach', body: 'Deliver funds into markets that are traditionally hard to reach.', icon: 'Globe2' },
    ],
    stat: [
      { value: '190+', label: 'Countries' },
      { value: '40+', label: 'Currencies' },
      { value: '<60s', label: 'Median settlement' },
    ],
  },
  '/solutions/global-payouts': {
    eyebrow: 'Solutions',
    title: 'Pay anyone, anywhere, at scale',
    subtitle:
      'Send funds to contractors, employees, creators and marketplaces worldwide from one balance — via bank transfer, local rails or stablecoins.',
    icon: 'Send',
    bullets: [
      'Bulk and scheduled payouts via API or dashboard',
      'Recipient choice: bank account, wallet or stablecoin',
      'Built-in compliance screening on every payout',
      'Live payout tracking and reconciliation',
    ],
    features: [
      { title: 'Mass payouts', body: 'Disburse thousands of payments in a single batch with automatic retries.', icon: 'Send' },
      { title: 'Flexible delivery', body: 'Let recipients pick how they want to be paid.', icon: 'Wallet' },
      { title: 'Reconciliation', body: 'Match every payout to its source with granular reporting.', icon: 'BarChart3' },
    ],
  },
  '/solutions/payment-collections': {
    eyebrow: 'Solutions',
    title: 'Collect local payments, globally',
    subtitle:
      'Accept payments from customers in their local currency and preferred method, then settle into one unified balance.',
    icon: 'Download',
    bullets: [
      'Local collection rails across key markets',
      'Virtual accounts to receive funds like a local business',
      'Automatic conversion into your settlement currency',
      'Unified reporting across every corridor',
    ],
    features: [
      { title: 'Local rails', body: 'Give customers a familiar, low-friction way to pay.', icon: 'Landmark' },
      { title: 'Virtual accounts', body: 'Receive payments as if you had a local bank account.', icon: 'CreditCard' },
      { title: 'Auto settlement', body: 'Consolidate collections into a single balance automatically.', icon: 'Zap' },
    ],
  },
  '/solutions/merchant-payments': {
    eyebrow: 'Solutions',
    title: 'Everything you need to get paid',
    subtitle:
      'Online checkout, payment links, QR payments and invoicing a complete merchant toolkit powered by banking and stablecoin rails.',
    icon: 'ShoppingCart',
    bullets: [
      'Hosted checkout and embeddable components',
      'Shareable payment links and QR codes',
      'Professional invoicing with reminders',
      'Instant settlement to fiat or stablecoins',
    ],
    features: [
      { title: 'Checkout', body: 'Convert more customers with a fast, secure checkout.', icon: 'ShoppingCart' },
      { title: 'Payment links', body: 'Get paid with a link — no code required.', icon: 'LinkIcon' },
      { title: 'Invoicing', body: 'Create and track invoices in any supported currency.', icon: 'FileText' },
    ],
  },
  '/solutions/treasury': {
    eyebrow: 'Solutions',
    title: 'Treasury-as-a-Service (TaaS)',
    subtitle: 'Optimized capital management for the modern business. Transform idle capital into operational fuel with intelligent infrastructure that automates, secures, and streamlines how your organization manages, moves, and grows its assets.',
    icon: 'Landmark',
    bullets: [
      'Unified Liquidity Management across fiat and stablecoins',
      'Programmable Collections with instant reconciliation',
      'Global Settlement Infrastructure at internet speed',
      'Risk-Managed Stability with integrated compliance',
    ],
    features: [
      { title: 'Unified Liquidity Management', body: 'Effortlessly manage fiat and stablecoin balances within a single interface. Our infrastructure allows you to maintain liquidity where you need it most, ensuring your capital is always available for deployment.', icon: 'Wallet' },
      { title: 'Programmable Collections', body: 'Provision dedicated bank details for fiat and stablecoin collections instantly. Whether you are receiving payments from global partners or internalizing cross-border settlements, Rombex automates the reconciliation process, reducing operational friction.', icon: 'CreditCard' },
      { title: 'Global Settlement Infrastructure', body: 'Leverage our robust financial network to settle transactions across borders. Our technology ensures that value moves with the speed of the internet, bypassing the delays inherent in legacy payment corridors.', icon: 'Globe2' },
    ],
  },
  '/solutions/crypto-defi': {
    eyebrow: 'Solutions',
    title: 'Modular stablecoin infrastructure',
    subtitle:
      'Onramps, offramps, custom stablecoin issuance and onchain financial services — built for teams expanding into crypto.',
    icon: 'Bitcoin',
    bullets: [
      'Fiat onramps and offramps in minutes',
      'Custom stablecoin issuance and management',
      'Onchain settlement across major networks',
      'Programmatic access to DeFi rails',
    ],
    features: [
      { title: 'On/Off ramps', body: 'Bridge fiat and crypto with a single API.', icon: 'RefreshCw' },
      { title: 'Issuance', body: 'Issue and manage stablecoins under your brand.', icon: 'Bitcoin' },
      { title: 'Onchain rails', body: 'Settle across the networks your users trust.', icon: 'Zap' },
    ],
  },
  '/solutions/banking-infrastructure': {
    eyebrow: 'Solutions',
    title: 'Banking infrastructure, without the banks',
    subtitle:
      'Virtual accounts, local rails and bank integrations that let you operate like a global bank — through one API.',
    icon: 'Building2',
    bullets: [
      'Named virtual accounts in multiple regions',
      'Direct access to local clearing systems',
      'Pre-integrated banking partners',
      'Programmable money movement',
    ],
    features: [
      { title: 'Virtual accounts', body: 'Spin up local accounts for collection and payout.', icon: 'CreditCard' },
      { title: 'Local rails', body: 'Connect directly to domestic payment systems.', icon: 'Landmark' },
      { title: 'Bank partners', body: 'Leverage our network of regulated institutions.', icon: 'Building2' },
    ],
  },

  '/products/orchestration': {
    eyebrow: 'Products',
    title: 'Payment orchestration engine',
    subtitle: 'Route every transaction across the optimal rail based on cost, speed, and reliability  automatically.',
    icon: 'Workflow',
    bullets: [
      'Smart routing across banking and stablecoin rails',
      'Automatic failover and retries',
      'Policy-based routing rules',
      'Full observability into every transaction',
    ],
    features: [
      { title: 'Smart routing', body: 'Choose the best rail per transaction in real time.', icon: 'Workflow' },
      { title: 'Resilience', body: 'Automatic failover keeps money moving.', icon: 'ShieldCheck' },
      { title: 'Insight', body: 'Trace every hop from initiation to settlement.', icon: 'BarChart3' },
    ],
  },
  '/products/wallets': {
    eyebrow: 'Products',
    title: 'Wallets for fiat and stablecoins',
    subtitle: 'Securely hold, manage and move balances across currencies and networks with enterprise controls.',
    icon: 'Wallet',
    bullets: [
      'Multi-currency and multi-network balances',
      'Programmatic wallet creation via API',
      'Granular permissions and approvals',
      'Institutional-grade custody options',
    ],
    features: [
      { title: 'Multi-asset', body: 'Hold fiat and stablecoins side by side.', icon: 'Wallet' },
      { title: 'API-first', body: 'Create and manage wallets programmatically.', icon: 'Code2' },
      { title: 'Secure', body: 'Custody and controls trusted by enterprises.', icon: 'ShieldCheck' },
    ],
  },
  '/products/virtual-accounts': {
    eyebrow: 'Products',
    title: 'Virtual accounts in every market',
    subtitle: 'Receive payments locally in multiple countries with named accounts your customers can trust.',
    icon: 'CreditCard',
    bullets: [
      'Unique named accounts per customer',
      'Instant reconciliation on inbound funds',
      'Local rails across key regions',
      'Automatic settlement and sweeping',
    ],
    features: [
      { title: 'Named accounts', body: 'Assign dedicated accounts per customer.', icon: 'CreditCard' },
      { title: 'Reconciliation', body: 'Match inbound funds automatically.', icon: 'BarChart3' },
      { title: 'Local presence', body: 'Look and feel local everywhere you operate.', icon: 'Globe2' },
    ],
  },
  '/products/payment-apis': {
    eyebrow: 'Products',
    title: 'Developer-first payment APIs',
    subtitle: 'Clean, well-documented REST APIs and SDKs to move money in minutes, not months.',
    icon: 'Code2',
    bullets: [
      'REST APIs with predictable responses',
      'SDKs for Node, Python, PHP, Go and Java',
      'Webhooks for real-time events',
      'Sandbox with test data',
    ],
    features: [
      { title: 'REST API', body: 'A consistent API surface across all products.', icon: 'Code2' },
      { title: 'SDKs', body: 'First-class libraries for popular languages.', icon: 'Terminal' },
      { title: 'Webhooks', body: 'React to money movement in real time.', icon: 'Zap' },
    ],
  },
  '/products/merchant-dashboard': {
    eyebrow: 'Products',
    title: 'The complete merchant dashboard',
    subtitle: 'Balances, payouts, payroll, payment links, invoices and analytics — everything your business needs in one place.',
    icon: 'LayoutDashboard',
    bullets: [
      'Unified balance across currencies and stablecoins',
      'Payouts, payroll, links and invoices in one view',
      'Live transactions and rate checker',
      'Team roles and permissions',
    ],
    features: [
      { title: 'Unified balance', body: 'See every currency and asset in one place.', icon: 'Landmark' },
      { title: 'Operate fast', body: 'Payouts, payroll and invoices from one screen.', icon: 'Send' },
      { title: 'Insights', body: 'Track performance with built-in analytics.', icon: 'BarChart3' },
    ],
  },
  '/products/user-wallet': {
    eyebrow: 'Products',
    title: 'A personal wallet people love',
    subtitle: 'A clean consumer wallet to hold, send and convert money and stablecoins across borders.',
    icon: 'Smartphone',
    bullets: [
      'Hold multiple currencies and stablecoins',
      'Send and receive instantly',
      'Convert at competitive rates',
      'Bank-grade security',
    ],
    features: [
      { title: 'Hold', body: 'Keep balances in the assets you choose.', icon: 'Wallet' },
      { title: 'Send', body: 'Move money to anyone in seconds.', icon: 'Send' },
      { title: 'Convert', body: 'Exchange currencies at great rates.', icon: 'RefreshCw' },
    ],
  },
  '/products/compliance-engine': {
    eyebrow: 'Products',
    title: 'Compliance built into the core',
    subtitle: 'KYC, KYB and AML screening embedded across the platform so you can scale with confidence.',
    icon: 'ShieldCheck',
    bullets: [
      'Automated identity and business verification',
      'Ongoing transaction monitoring',
      'Sanctions and watchlist screening',
      'Audit-ready reporting',
    ],
    features: [
      { title: 'KYC / KYB', body: 'Verify people and businesses quickly.', icon: 'ShieldCheck' },
      { title: 'Monitoring', body: 'Detect risk with continuous screening.', icon: 'Activity' },
      { title: 'Reporting', body: 'Stay audit-ready with detailed logs.', icon: 'FileText' },
    ],
  },
  '/products/settlement-engine': {
    eyebrow: 'Products',
    title: 'Settlement at the speed of the internet',
    subtitle: 'Fast, deterministic settlement across banking and stablecoin rails with full traceability.',
    icon: 'Zap',
    bullets: [
      'Near-instant settlement on supported rails',
      'Deterministic status for every transaction',
      'Netting and batching for efficiency',
      'End-to-end traceability',
    ],
    features: [
      { title: 'Speed', body: 'Settle in seconds where rails allow.', icon: 'Zap' },
      { title: 'Certainty', body: 'Know the exact state of every payment.', icon: 'ShieldCheck' },
      { title: 'Efficiency', body: 'Net and batch to reduce cost.', icon: 'Workflow' },
    ],
  },
  '/products/analytics': {
    eyebrow: 'Products',
    title: 'Analytics for money movement',
    subtitle: 'Understand volumes, costs, success rates and corridors with reporting built for finance and product teams.',
    icon: 'BarChart3',
    bullets: [
      'Real-time dashboards and exports',
      'Corridor and rail performance',
      'Cost and FX analytics',
      'Custom reports and alerts',
    ],
    features: [
      { title: 'Dashboards', body: 'Monitor performance at a glance.', icon: 'BarChart3' },
      { title: 'Exports', body: 'Pull data into your own tools.', icon: 'Download' },
      { title: 'Alerts', body: 'Get notified when metrics move.', icon: 'Activity' },
    ],
  },
}

export function getPageContent(href: string): PageContent {
  const found = pageContent[href]
  if (found) return found
  const item = findMenuItem(href)
  return {
    eyebrow: 'Rombex',
    title: item?.title ?? 'Rombex',
    subtitle: item?.description ?? 'Global financial infrastructure for businesses and individuals.',
    icon: item?.icon ?? 'Sparkles',
    bullets: [
      'Unified access to banking and stablecoin rails',
      'Developer-first APIs and SDKs',
      'Global coverage with local presence',
      'Compliance and security by default',
    ],
    features: [
      { title: 'Global', body: 'Operate across borders from one platform.', icon: 'Globe2' },
      { title: 'Fast', body: 'Move money in seconds, not days.', icon: 'Zap' },
      { title: 'Secure', body: 'Enterprise-grade security and compliance.', icon: 'ShieldCheck' },
    ],
  }
}
